// Map #3 — Rust Canyon (sim half). Red-orange badlands under a hard noon sun: banded hoodoos and mesas (the 'spire'
// archetype), fallen sandstone blocks and iron-ore boulders ('boulder'), red rubble ('rock'), bleached dead trees ('tree')
// and dry scrub / straw tufts / old bone piles ('bush'). Broad flat-topped plateaus with gentle ramps and dry washes
// (the hill spec below saturates big mounds into tablelands). Nothing burns.
// Hazards (owner 2026-11-02: light, telegraphed, the same for the hero and the foes):
//   DUST STORM  every 2.5 min a wall of dust rolls across the whole arena during a 12 s telegraph (the heading alternates,
//               it is carried in the zone's `rot` for the renderer); then the arena sits inside the storm for 14 s:
//               visibility 50 %, everyone ~6 % slower (a global fog zone: no ground decal, the wall is the telegraph)
//   ROCKFALL    loose rock drops at the foot of a few hoodoos every 1.5–2 min (crush: 18 damage, regular foes under it are
//               flattened; 2.2 s of falling pebbles first)
//   ARCH FALL   two natural stone arches stand in the arena; each collapses once, mid-run (crush rectangle under its span,
//               24 damage, 3.2 s of cracking first)
// Pure (check:ip): no three, no DOM, no unseeded randomness. Everything random comes from ctx.rng or the terrain seed.
import type { HazardDef } from '../../core/hazards';
import type { HillSpec, MapCoreDef } from '../../core/mapdef';
import type { Prop, VariantDef } from '../../core/terrain';

/** Tablelands: big mounds that the soft limit (tanh) flattens into plateaus, separated by shallow dry washes. */
export const RC_HILLS: HillSpec = { mounds: 14, dip: 0.34, rad: [17, 31], dipRad: [9, 16], amp: [2.8, 4.6], dipAmp: [0.8, 1.5], swell: 0.5, max: 3.4, min: -1.5 };

/** Dead trees (index = render variant): bleached snags, low forked trunks, broken stumps. r = trunk base radius at s = 1. */
export const RC_TREE_VARIANTS: VariantDef[] = [
  { name: 'snag', r: 0.42, s: [0.9, 1.25], h: [0.9, 1.15], hp: 0.9, w: 3 },
  { name: 'forked', r: 0.5, s: [0.85, 1.15], h: [0.85, 1.05], hp: 1, w: 2 },
  { name: 'stump', r: 0.5, s: [0.8, 1.1], h: [0.8, 1.0], hp: 0.7, w: 1.5 },
];
/** Low breakables (index = render variant): dusty sage scrub, dry straw tufts, bleached bone piles. r includes BUSH_K. */
export const RC_BUSH_VARIANTS: VariantDef[] = [
  { name: 'sage', r: 0.95, s: [0.8, 1.2], h: [0.85, 1.1], hp: 1, w: 3 },
  { name: 'tuft', r: 0.6, s: [0.8, 1.2], h: [0.9, 1.2], hp: 0.6, w: 3 },
  { name: 'bones', r: 0.85, s: [0.85, 1.15], h: [1, 1], hp: 0.8, w: 1.4 },
];

/** Hazard tuning (tests and the renderer read these). */
export const RC_STORM = { warn: 12, active: 14, power: 0.5, first: 110, gap: 150, fronts: 2 } as const;
export const RC_ROCKFALL = { sites: 6, power: 18, warn: 2.2, first: [55, 170] as [number, number], every: [85, 125] as [number, number] } as const;
export const RC_ARCH = { count: 2, half: 7.5, w: 2.6, power: 24, warn: 3.2 } as const;

const TAU = Math.PI * 2;
/** rockfall sites were authored on the 110 m arena; they scale with the arena area (owner 2026-11-02: 160 m). The two
 *  arches are landmark set pieces and stay two. */
export const rcRockfallSites = (arenaR: number): number => Math.round(RC_ROCKFALL.sites * Math.max(1, (arenaR / 110) ** 2));
const near = (props: readonly Prop[], x: number, z: number, pad: number, solidOnly = true) =>
  props.some((p) => (!solidOnly || p.solid) && (p.x - x) ** 2 + (p.z - z) ** 2 < (p.r + pad) ** 2);

export const MAP: MapCoreDef = {
  id: 'rust-canyon',
  name: 'Rust Canyon',
  tagline: 'Banded red mesas under a hard noon sun. Stone arches crumble, rocks rain from the hoodoos and dust storms roll through.',
  order: 3,
  status: 'ready',
  time: { default: 'day', day: 'noon' },
  unlock: { after: 'shattered-metropolis', seconds: 480 },
  terrain: { hills: RC_HILLS, props: [
    // hoodoos and mesas: fewer than map #1 per area but chunkier, so the open fighting ground stays wide
    { kind: 'spire', count: 6, rMin: 28, inset: 7, size: [3.9, 6.2], clear: 13, h: [10, 18], hpPer: 4.5 },
    { kind: 'boulder', count: 20, rMin: 13, inset: 3, size: [1.5, 2.7], clear: 4, h: [0.75, 1.25], hpPer: 2 },
    { kind: 'tree', count: 14, rMin: 11, inset: 3, size: [0.5, 0.5], clear: 2.5, h: [1, 1], variants: RC_TREE_VARIANTS },
    { kind: 'rock', count: 56, rMin: 8, inset: 2, size: [0.45, 0.95], clear: 0.8, h: [0.7, 1.2] },
    { kind: 'bush', count: 50, rMin: 9, inset: 2, size: [1, 1], clear: 0, h: [1, 1], variants: RC_BUSH_VARIANTS, layout: 'clumps' },
  ] },
  fire: false,
  boss: { name: 'OREHEART BEHEMOTH', hpMul: 1.15 },
  hazards: ({ rng, arenaR, runLen, props }) => {
    const out: HazardDef[] = [];
    // ---- DUST STORM: fronts alternate between two headings (rot = travel direction of the dust wall, render only)
    const S = RC_STORM;
    for (let f = 0; f < S.fronts; f++) {
      out.push({ kind: 'fog', x: 0, z: 0, r: 0, global: true, rot: rng.range(0, TAU), start: S.first + f * S.gap, warn: S.warn, active: S.active,
        every: S.gap * S.fronts, power: S.power, tag: 'storm' });
    }
    // ---- ROCKFALL at the foot of a few hoodoos (never on the hoodoo itself, never near the spawn)
    const spires = props.filter((p) => p.kind === 'spire');
    for (let i = spires.length - 1; i > 0; i--) { const j = rng.int(i + 1); [spires[i], spires[j]] = [spires[j], spires[i]]; }
    let sites = 0;
    for (const sp of spires) {
      if (sites >= rcRockfallSites(arenaR)) break;
      const r = rng.range(3, 3.8), a0 = rng.range(0, TAU);
      for (let t = 0; t < 8; t++) {
        const a = a0 + t * 0.785, d = sp.r + r + 0.8, x = sp.x + Math.cos(a) * d, z = sp.z + Math.sin(a) * d, dc = Math.hypot(x, z);
        if (dc < 18 || dc > arenaR - r - 3) continue;
        if (props.some((p) => p !== sp && p.kind === 'spire' && (p.x - x) ** 2 + (p.z - z) ** 2 < (p.r + r + 0.5) ** 2)) continue;
        out.push({ kind: 'crush', x, z, r, start: rng.range(RC_ROCKFALL.first[0], RC_ROCKFALL.first[1]), warn: RC_ROCKFALL.warn, active: 0,
          every: rng.range(RC_ROCKFALL.every[0], RC_ROCKFALL.every[1]), power: RC_ROCKFALL.power, tag: 'rockfall' });
        sites++; break;
      }
    }
    // ---- NATURAL ARCHES: two free-standing spans in the open; each comes down once (first in the opening third of the
    // run, the second around the middle). Legs stay clear of solid props, the span of hoodoos.
    const placed: { x: number; z: number }[] = [];
    for (let i = 0; i < RC_ARCH.count; i++) {
      for (let t = 0; t < 60; t++) {
        const a = rng.range(0, TAU), d = rng.range(34, arenaR - 22), x = Math.cos(a) * d, z = Math.sin(a) * d, rot = rng.range(0, Math.PI);
        const L = RC_ARCH.half + 0.6, lx = Math.cos(rot) * L, lz = Math.sin(rot) * L;
        if (near(props, x + lx, z + lz, 2.6) || near(props, x - lx, z - lz, 2.6) || near(props, x, z, RC_ARCH.half + 1)) continue;
        if (placed.some((q) => Math.hypot(q.x - x, q.z - z) < 50)) continue;
        placed.push({ x, z });
        const start = Math.max(120, runLen * (i === 0 ? rng.range(0.16, 0.28) : rng.range(0.42, 0.58)));
        out.push({ kind: 'crush', x, z, r: 0, l: RC_ARCH.half, w: RC_ARCH.w, rot, start, warn: RC_ARCH.warn, active: 0, power: RC_ARCH.power, tag: 'arch' });
        break;
      }
    }
    return out;
  },
};
